import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  IconButton,
  Box,
  Autocomplete,
  Chip,
  MenuItem,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { colors, typography, inputs } from "../../theme/designSystem";
import { PlanData } from "../../interfaces/PlanData";
import { benefitsService } from "../../services/benefitsService";
import { useQuery } from "@tanstack/react-query";

// Lista de funcionalidades disponíveis
// AVAILABLE_FEATURES agora é um estado preenchido pelo backend

// Lista de funcionalidades disponíveis
// const AVAILABLE_FEATURES = [
//   "Agenda básica",
//   "Agenda avançada",
//   "Cadastro de pacientes",
//   "Múltiplos profissionais",
//   "Relatórios simples",
//   "Relatórios completos",
//   "Integração WhatsApp",
//   "API personalizada",
//   "Suporte 24/7",
//   "Customizações",
//   "Recursos ilimitados",
//   "Backup automático",
//   "Segurança avançada",
//   "Treinamento da equipe",
// ];

// Props do componente
interface PlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: PlanData) => void;
  mode: "create" | "edit";
  initialData?: Partial<PlanData>;
  title?: string;
}

const PlanModal: React.FC<PlanModalProps> = ({
  isOpen,
  onClose,
  onSave,
  mode,
  initialData = {},
  title,
}) => {
  // Estado do formulário
  // Usa react-query para cachear funcionalidades disponíveis
  const { data: availableFeatures = [] } = useQuery<string[]>({
    queryKey: ["benefits"],
    queryFn: async () => {
      const benefits = await benefitsService.getAll();
      return benefits.map((b: any) => b.description);
    },
    staleTime: 1000 * 60 * 10, // 10 minutos
  });
  const [formData, setFormData] = useState<PlanData>({
    name: "",
    description: "",
    price: 0,
    duration: 12,
    maxUsers: 1,
    monthlyValue: 0,
    annuallyValue: 0,
    features: [],
    status: "Ativo",
    ...initialData,
  });
  // Remover checkedFeatures state, not needed
  // const [checkedFeatures, setCheckedFeatures] = useState<boolean>(false);

  // Carrega funcionalidades disponíveis do backend e inicializa features
  useEffect(() => {
    if (!Array.isArray(availableFeatures) || availableFeatures.length === 0)
      return;

    if (isOpen && mode === "edit" && initialData) {
      setFormData({
        name: initialData.name ?? "",
        description: initialData.description ?? "",
        price: initialData.price ?? 0,
        duration: initialData.duration ?? 12,
        maxUsers: initialData.maxUsers ?? 1,
        monthlyValue: initialData.monthlyValue ?? 0,
        annuallyValue: initialData.annuallyValue ?? 0,
        status: initialData.status ?? "Ativo",
        features: availableFeatures.map((name) => {
          const label =
            typeof name === "string" ? name : (name as any).description;
          let isChecked = false;
          if (Array.isArray(initialData.features)) {
            isChecked = initialData.features.some((f: any) => {
              if (typeof f === "string") {
                return f === label;
              } else {
                return f.name === label && f.included === true;
              }
            });
          }
          return { name: label, included: isChecked };
        }),
      });
    } else if (isOpen && mode === "create") {
      // setFormData((prev) => {
      //   // Garante que todos os benefícios estejam presentes, mesmo se a lista mudar
      //   const features = availableFeatures.map((name) => {
      //     const label =
      //       typeof name === "string" ? name : (name as any).description;
      //     // Se já existe no estado, mantém o valor marcado
      //     const found = prev.features.find((f) => f.name === label);
      //     return found ? found : { name: label, included: false };
      //   });
      //   return {
      //     name: "",
      //     description: "",
      //     price: 0,
      //     duration: 12,
      //     maxUsers: 1,
      //     monthlyValue: 0,
      //     annuallyValue: 0,
      //     status: "Ativo",
      //     features,
      //   };
      // });
    }
  }, [availableFeatures, initialData, mode, isOpen]);
  // Função handleFeatureCheckboxChange removida pois não é utilizada
  // Função para formatar preço
  const formatCurrency = (value: number) => {
    return value?.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Função para parse do preço
  const parseCurrency = (value: string) => {
    const cleanValue = value.replace(/[^\d,]/g, "").replace(",", ".");
    return parseFloat(cleanValue) || 0;
  };

  // Limpar formulário ao fechar
  const handleClose = () => {
    setFormData({
      name: "",
      description: "",
      price: 0,
      duration: 12,
      maxUsers: 1,
      monthlyValue: 0,
      annuallyValue: 0,
      features: availableFeatures.map((name) => ({ name, included: false })),
      status: "Ativo",
    });
    onClose();
  };

  // Salvar dados
  const handleSave = () => {
    // Sincroniza features com o estado atual do formData
    onSave({ ...formData, features: formData.features });
    handleClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
          maxHeight: "90vh",
        },
      }}
    >
      {/* Cabeçalho do modal */}
      <DialogTitle
        sx={{
          backgroundColor: colors.primary,
          color: colors.white,
          padding: "1.5rem",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h6"
          component="h3"
          sx={{
            fontSize: "1.4rem",
            fontWeight: typography.fontWeight.semibold,
            margin: 0,
          }}
        >
          {title || (mode === "create" ? "Novo Plano" : "Editar Plano")}
        </Typography>
        <IconButton
          onClick={handleClose}
          sx={{
            color: colors.white,
            padding: "0.25rem",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.1)",
            },
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      {/* Conteúdo do modal */}
      <DialogContent
        sx={{ padding: "1.5rem !important", paddingTop: "2rem !important" }}
      >
        {/* Primeira linha: Nome e Status */}
        <Box
          sx={{
            display: "flex",
            gap: "0.75rem",
            marginBottom: "1.5rem",
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ flex: "2 1 300px", minWidth: "200px" }}>
            <TextField
              label="Nome do Plano"
              fullWidth
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              placeholder="Ex: Plano Básico"
              InputLabelProps={{
                shrink: true,
                sx: {
                  fontSize: inputs.default.labelFontSize,
                  color: inputs.default.labelColor,
                  backgroundColor: inputs.default.labelBackground,
                  padding: inputs.default.labelPadding,
                  "&.Mui-focused": {
                    color: inputs.default.focus.labelColor,
                  },
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: inputs.default.height,
                  fontSize: inputs.default.fontSize,
                  "& fieldset": {
                    borderColor: inputs.default.borderColor,
                    legend: {
                      maxWidth: inputs.default.legendMaxWidth,
                    },
                  },
                  "&:hover fieldset": {
                    borderColor: inputs.default.hover.borderColor,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: inputs.default.focus.borderColor,
                    boxShadow: inputs.default.focus.boxShadow,
                  },
                },
              }}
            />
          </Box>

          <Box sx={{ flex: "1 1 150px", minWidth: "120px" }}>
            <TextField
              select
              label="Status"
              fullWidth
              value={formData.status}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  status: e.target.value as "Ativo" | "Inativo",
                })
              }
              InputLabelProps={{
                shrink: true,
                sx: {
                  fontSize: inputs.select.labelFontSize,
                  color: inputs.select.labelColor,
                  backgroundColor: inputs.select.labelBackground,
                  padding: inputs.select.labelPadding,
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: inputs.select.height,
                  fontSize: inputs.select.fontSize,
                  "& fieldset": {
                    borderColor: inputs.select.borderColor,
                  },
                },
              }}
            >
              <MenuItem value="Ativo">Ativo</MenuItem>
              <MenuItem value="Inativo">Inativo</MenuItem>
            </TextField>
          </Box>
        </Box>

        {/* Segunda linha: Descrição */}
        <Box sx={{ marginBottom: "1.5rem" }}>
          <TextField
            label="Descrição"
            fullWidth
            multiline
            rows={3}
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            placeholder="Descreva as características do plano..."
            InputLabelProps={{
              shrink: inputs.multiline.labelShrink,
              sx: {
                fontSize: inputs.multiline.labelFontSize,
                color: inputs.multiline.labelColor,
                backgroundColor: inputs.multiline.labelBackground,
                padding: inputs.multiline.labelPadding,
                "&.Mui-focused": {
                  color: colors.primary,
                },
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                position: inputs.multiline.position,
                opacity: inputs.multiline.opacity,
                alignItems: inputs.multiline.alignItems,
                fontSize: inputs.multiline.fontSize,
                minHeight: inputs.multiline.minHeight,
                maxHeight: inputs.multiline.maxHeight,
                overflow: inputs.multiline.overflow,
                padding: 0,
                "& fieldset": {
                  borderColor: inputs.multiline.borderColor,
                },
                "&:hover fieldset": {
                  borderColor: inputs.multiline.borderColor,
                },
                "&.Mui-focused fieldset": {
                  borderColor: colors.primary,
                },
                "& textarea": {
                  wordWrap: inputs.multiline.wordWrap,
                  whiteSpace: inputs.multiline.whiteSpace,
                  padding: inputs.multiline.inputPadding,
                  height: inputs.multiline.textareaHeight,
                  maxHeight: inputs.multiline.textareaMaxHeight,
                  overflow: `${inputs.multiline.textareaOverflow} !important`,
                  boxSizing: inputs.multiline.textareaBoxSizing,
                  "&::-webkit-scrollbar": {
                    width: inputs.multiline.scrollbarWidth,
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: inputs.multiline.scrollbarTrackColor,
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: inputs.multiline.scrollbarThumbColor,
                    borderRadius: "4px",
                    "&:hover": {
                      backgroundColor:
                        inputs.multiline.scrollbarThumbHoverColor,
                    },
                  },
                },
              },
            }}
          />
        </Box>

        {/* Terceira linha: Valores Mensal e Anual */}
        <Box
          sx={{
            display: "flex",
            gap: "0.75rem",
            marginBottom: "1.5rem",
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ flex: "1 1 200px", minWidth: "150px" }}>
            <TextField
              label="Valor mensal (R$)"
              fullWidth
              value={formatCurrency(formData.price)}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  price: parseCurrency(e.target.value),
                })
              }
              placeholder="0,00"
              InputLabelProps={{
                shrink: true,
                sx: {
                  fontSize: inputs.default.labelFontSize,
                  color: inputs.default.labelColor,
                  backgroundColor: inputs.default.labelBackground,
                  padding: inputs.default.labelPadding,
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: inputs.default.height,
                  fontSize: inputs.default.fontSize,
                  "& fieldset": {
                    borderColor: inputs.default.borderColor,
                  },
                },
              }}
            />
          </Box>

          <Box sx={{ flex: "1 1 200px", minWidth: "150px" }}>
            <TextField
              label="Valor anual (R$)"
              fullWidth
              value={formatCurrency(formData.annuallyValue)}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  annuallyValue: parseCurrency(e.target.value),
                })
              }
              placeholder="0,00"
              InputLabelProps={{
                shrink: true,
                sx: {
                  fontSize: inputs.default.labelFontSize,
                  color: inputs.default.labelColor,
                  backgroundColor: inputs.default.labelBackground,
                  padding: inputs.default.labelPadding,
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: inputs.default.height,
                  fontSize: inputs.default.fontSize,
                  "& fieldset": {
                    borderColor: inputs.default.borderColor,
                  },
                },
              }}
            />
          </Box>
        </Box>

        {/* Quarta linha: Duração e Máximo de Usuários */}
        <Box
          sx={{
            display: "flex",
            gap: "0.75rem",
            marginBottom: "1.5rem",
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ flex: "1 1 200px", minWidth: "150px" }}>
            <TextField
              type="number"
              label="Duração (meses)"
              fullWidth
              inputProps={{ min: 1 }}
              value={formData.duration}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  duration: parseInt(e.target.value) || 1,
                })
              }
              placeholder="12"
              InputLabelProps={{
                shrink: true,
                sx: {
                  fontSize: inputs.default.labelFontSize,
                  color: inputs.default.labelColor,
                  backgroundColor: inputs.default.labelBackground,
                  padding: inputs.default.labelPadding,
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: inputs.default.height,
                  fontSize: inputs.default.fontSize,
                  "& fieldset": {
                    borderColor: inputs.default.borderColor,
                  },
                },
              }}
            />
          </Box>

          <Box sx={{ flex: "1 1 200px", minWidth: "150px" }}>
            <TextField
              type="number"
              label="Máx. Usuários"
              fullWidth
              inputProps={{ min: 1 }}
              value={formData.maxUsers}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  maxUsers: parseInt(e.target.value) || 1,
                })
              }
              placeholder="5"
              InputLabelProps={{
                shrink: true,
                sx: {
                  fontSize: inputs.default.labelFontSize,
                  color: inputs.default.labelColor,
                  backgroundColor: inputs.default.labelBackground,
                  padding: inputs.default.labelPadding,
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: inputs.default.height,
                  fontSize: inputs.default.fontSize,
                  "& fieldset": {
                    borderColor: inputs.default.borderColor,
                  },
                },
              }}
            />
          </Box>
        </Box>

        {/* Funcionalidades */}
        <Box sx={{ marginBottom: "2rem" }}>
          <FormControl component="fieldset" sx={{ width: "100%" }}>
            <FormLabel
              component="legend"
              sx={{ color: colors.textSecondary, fontWeight: 500 }}
            >
              Selecionar funcionalidades do plano
            </FormLabel>
            <FormGroup
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 2,
                alignItems: "start",
              }}
            >
              {availableFeatures.map((feature) => {
                const label =
                  typeof feature === "string"
                    ? feature
                    : (feature as any).description;
                const idx = formData.features.findIndex(
                  (f) => f.name === label
                );
                const checked =
                  idx !== -1 ? !!formData.features[idx].included : false;
                return (
                  <FormControlLabel
                    key={label}
                    control={
                      <Checkbox
                        checked={checked}
                        onChange={(e) => {
                          setFormData((prev) => {
                            const exists = prev.features.find(
                              (f) => f.name === label
                            );
                            if (exists) {
                              return {
                                ...prev,
                                features: prev.features.map((f) =>
                                  f.name === label
                                    ? { ...f, included: e.target.checked }
                                    : f
                                ),
                              };
                            } else {
                              return {
                                ...prev,
                                features: [
                                  ...prev.features,
                                  { name: label, included: e.target.checked },
                                ],
                              };
                            }
                          });
                        }}
                        color="primary"
                      />
                    }
                    label={label}
                    sx={{
                      textAlign: "left",
                      justifyContent: "flex-start",
                      m: 0,
                    }}
                  />
                );
              })}
            </FormGroup>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          padding: "1.5rem 2rem",
          borderTop: `1px solid ${colors.backgroundAlt}`,
          backgroundColor: colors.background,
          gap: "1rem",
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            padding: "0.75rem 1.5rem",
            border: `1px solid ${colors.border}`,
            borderRadius: "6px",
            backgroundColor: colors.white,
            color: colors.textSecondary,
            fontSize: "1rem",
            fontWeight: typography.fontWeight.medium,
            textTransform: "none",
            "&:hover": {
              backgroundColor: colors.background,
              borderColor: "#adb5bd",
            },
          }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          sx={{
            padding: "0.75rem 1.5rem",
            borderRadius: "6px",
            backgroundColor: colors.primary,
            color: colors.white,
            fontSize: "1rem",
            fontWeight: typography.fontWeight.medium,
            textTransform: "none",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: "#029AAB",
              boxShadow: "none",
              transform: "translateY(-1px)",
            },
          }}
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlanModal;
